import cn from 'classnames'
import React from 'react'

import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Link} from '@/i18n'
import {EXPAND_TRANSITION, HOVER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {QueryLink} from '@/utils/constants'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {BrandProtectionCardContent} from './BrandProtectionCardContent'

type BrandProtectionCardProps = {
  labels: string[]
  title: string
  subtitle?: string
  description: string
  price?: number
  link?: string | QueryLink
  buttonText: string
  buttonVariant: ButtonVariant
  priceNote?: string
  isHidden: boolean
  isExpanded: boolean
  onClick?: () => void
}

export const BrandProtectionCard: React.FC<React.PropsWithChildren<BrandProtectionCardProps>> = ({
  labels,
  title,
  subtitle,
  description,
  price,
  link,
  buttonText,
  buttonVariant,
  priceNote,
  isHidden,
  isExpanded,
  onClick,
}) => {
  const trackVerificationButtonClick = () => analytics.track('VerificationMainServices')

  return (
    <>
      <div className={cn('card', isHidden && 'hidden', isExpanded && 'expanded')}>
        {link ? (
          <Link href={link} legacyBehavior>
            <a
              data-test="BrandProtectionCard"
              className="card-wrapper"
              onClick={trackVerificationButtonClick}
              role="button"
              tabIndex={0}
            >
              <BrandProtectionCardContent
                labels={labels}
                title={title}
                subtitle={subtitle}
                description={description}
                price={price}
                buttonText={buttonText}
                buttonVariant={buttonVariant}
                priceNote={priceNote}
              />
            </a>
          </Link>
        ) : onClick ? (
          <div className="card-wrapper" onClick={onClick} role="button" tabIndex={0}>
            <BrandProtectionCardContent
              labels={labels}
              title={title}
              subtitle={subtitle}
              description={description}
              price={price}
              buttonText={buttonText}
              buttonVariant={buttonVariant}
              priceNote={priceNote}
            />
          </div>
        ) : null}
      </div>

      <style jsx>{`
        .card {
          width: 100%;
          max-width: 353px;
          margin: ${theme.spacing[3]};
        }

        .card:nth-child(2) {
          order: -1;
        }

        .card-wrapper {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 100%;
          padding: ${theme.spacing[4]};
          background: ${theme.palette.background.default};
          text-decoration: none;
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
          border-radius: 16px;
          transition: all ${HOVER_TRANSITION}ms ease-in-out;
          cursor: pointer;
        }

        .card-wrapper:hover {
          transform: scale(1.0313);
        }

        @media ${TABLET_SCREENSIZE} {
          .card {
            margin: ${theme.spacing[2.5]};
          }

          .card:nth-child(2) {
            order: 0;
          }

          .card:first-of-type {
            max-width: inherit;
          }

          .card:first-of-type .card-wrapper {
            max-width: 360px;
            margin: 0 auto;
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .card {
            margin: 0;
          }

          .hidden {
            max-height: 0;
            opacity: 0;
            visibility: hidden;
          }

          .expanded {
            max-height: 400px;
            opacity: 1;
            visibility: visible;
            transition: opacity ${EXPAND_TRANSITION}ms ease-in-out;
          }

          .card.hidden:not(:first-of-type) {
            margin-top: 0;
          }

          .card.expanded:not(:first-of-type) {
            margin-top: ${theme.spacing[4]};
          }
        }
      `}</style>
    </>
  )
}
